import {
    ArrowLeft,
    Globe,
    WarningCircle,
} from "@phosphor-icons/react";
import { format, parse } from "date-fns";
import { Link, useParams } from "react-router-dom";
import { appPaths } from "../Routes";
import {
    useDataSourceById,
    useSetDataSourceCalculation,
} from "../api/data-sources";
import Button from "../components/Button";
import ContentContainer from "../components/ContentContainer";
import DialogBox, { useDialogBoxState } from "../components/DialogBox";
import IconButton from "../components/IconButton";
import MainLayout from "../components/MainLayout";
import Separator from "../components/Separator";
import SetCalculationForm from "../components/SetCalculationForm";
import { SetCalculationFormValues } from "../components/SetCalculationForm/SetCalculationFormValues";
import Typography from "../components/Typography";
import { errorToast, successToast } from "../toast";
import Avatar from "../components/Avatar";
import { getInitialsFromDisplayName } from "../utils/getInitialsFromDisplayNames";
import ConnectionTypeChips from "../components/ConnectionTypeChips";
import { RequireEmissionsSourceEvidenceTable } from "../components/RequiredEvidenceTable";
import ActionMenu from "../components/ActionMenu";
import SectionTitle from "../components/SectionTitle";
import { ConfidenceName, confidenceOptions } from "../constants";
import DataSourceCalculationCards from "../components/DataSourceCalculationFactorCards";
import Chip from "../components/Chip";

const formattedInitialCalculationActiveFromDate = "01/01/0001";

const DataSourceDetailPage = () => {
    const { id } = useParams();
    const setCalculationDialogState = useDialogBoxState();

    const { dataSource, isLoading, refetch } = useDataSourceById(Number(id));
    const [setCalculation, isSettingCalculation] = useSetDataSourceCalculation(
        Number(id)
    );

    const handleSetCalculationFormSubmission = async (
        value: SetCalculationFormValues
    ) => {
        if (value.activeFrom === null) return;

        const response = await setCalculation({
            ...value,
            activeFrom: format(value.activeFrom, "yyyy-MM-dd"),
        });

        if (!response.success) {
            errorToast(
                "There was a problem creating the emissions factor - please try again"
            );
            return;
        }

        refetch();
        successToast("Emissions factor set");
        setCalculationDialogState.close();
    };

    let formattedApplicableFromDate = dataSource?.currentCalculationActiveFrom
        ? format(
              parse(
                  dataSource.currentCalculationActiveFrom,
                  "yyyy-MM-dd",
                  new Date()
              ),
              "dd/MM/yyyy"
          )
        : "--";

    if (
        formattedApplicableFromDate ===
        formattedInitialCalculationActiveFromDate
    ) {
        formattedApplicableFromDate = "Initial";
    }

    const confidenceName = dataSource?.currentEmissionsFactorConfidenceId && 
        confidenceOptions.find(option => option.value === dataSource.currentEmissionsFactorConfidenceId)?.label as ConfidenceName;

    if (!id) return null;

    return (
        <MainLayout isLoading={isLoading}>
            {!dataSource && (
                <ContentContainer className="py-10 text-center">
                    <p>No data source found matching this id</p>
                </ContentContainer>
            )}
            {dataSource && (
                <ContentContainer className="pt-14 pb-11">
                    <div className="flex flex-col sm:flex-row gap-6 items-center pb-5">
                        <div className="flex items-center">
                            <IconButton
                                label="Back"
                                icon={ArrowLeft}
                                iconSize="large"
                                className="mr-3"
                                onClick={() => window.history.back()}
                            />
                            <Typography variant="headline-small" component="h1">
                                {dataSource.locationName} {dataSource.name}
                            </Typography>
                        </div>
                        <div className="ml-auto flex items-center gap-2 font-semibold">
                            <Globe weight="light" size={24} />
                            <Typography
                                variant="headline-small"
                                component="span"
                                className="text-deep-ocean-20"
                            >
                                Scope {dataSource.scope}
                            </Typography>
                        </div>
                    </div>
                    <Separator className="mb-6" />
                    <div className="flex justify-between">
                        <div className="flex gap-4 items-center">
                            <ConnectionTypeChips {...dataSource} />
                        </div>
                        <ActionMenu
                            label="Actions"
                            options={[
                                {
                                    label: "View emissions source",
                                    to: appPaths.emissionsSourceDetail(
                                        `${dataSource.emissionsSourceId}`
                                    ),
                                },
                                {
                                    label: "Add activity data",
                                    to: appPaths.dataSourceAddActivity(id),
                                    disabled:
                                        !!dataSource.activityMetadataTypeId,
                                },
                            ]}
                        />
                    </div>
                    <SectionTitle title="Calculation">
                        {dataSource.currentCalculationActiveFrom ? 
                        <Chip colour="green" className="mr-auto ml-3">
                            Applicable from {dataSource.currentCalculationActiveFrom}
                        </Chip> : 
                        <></>}
                    </SectionTitle>
                    <div className="flex flex-col sm:flex-row justify-between mb-9">
                        <div className="flex gap-6">
                            {dataSource.currentCalculationSetAt && (
                                <Button
                                    variant="outline"
                                    onClick={setCalculationDialogState.open}
                                >
                                    Set emissions factor
                                </Button>
                            )}
                            {(dataSource.currentCalculationSetAt ||
                                dataSource.calculationHistoryCount !== 0) && (
                                <Link
                                    to={appPaths.dataSourceEmissionsFactorHistory(
                                        id
                                    )}
                                >
                                    <Button
                                        className="w-full"
                                        variant="outline"
                                        disabled={
                                            dataSource.calculationHistoryCount ===
                                            0
                                        }
                                    >
                                        View history
                                    </Button>
                                </Link>
                            )}
                        </div>
                    </div>
                    {dataSource.currentCalculationSetAt ? (
                        <>
                            <div className="flex items-center sm:space-x-4">
                                <div className="hidden sm:block">
                                    <Avatar
                                        initials={getInitialsFromDisplayName(
                                            dataSource.currentCalculationSetByDisplayName
                                        )}
                                    />
                                </div>
                                <div>
                                    <Typography
                                        variant="body-large"
                                        component="p"
                                        className="w-full max-w-full overflow-hidden overflow-ellipsis"
                                    >
                                        <b>Submitted by:</b>{" "}
                                        {dataSource.currentCalculationSetByDisplayName ??
                                            "--"}
                                    </Typography>
                                    <div className="flex">
                                        <Typography
                                            variant="body-large"
                                            component="p"
                                            className="overflow-hidden overflow-ellipsis mr-4"
                                        >
                                            <b>Submitted on date:</b>{" "}
                                            {dataSource.currentCalculationSetAt
                                                ? format(
                                                    new Date(
                                                        dataSource.currentCalculationSetAt
                                                    ),
                                                    "dd/MM/yyyy"
                                                )
                                                : "--"}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-surface-light-1 rounded-xl p-6  mt-8">
                                <div className="flex flex-row py-3">
                                    <Typography
                                        variant="body-large"
                                        component="span"
                                        className="overflow-hidden overflow-ellipsis mr-3"
                                    >
                                        <b>Material:</b>{" "}
                                        {dataSource.rawMaterialName ?? "--"}
                                    </Typography>
                                    <Typography
                                        variant="body-large"
                                        component="span"
                                        className="overflow-hidden overflow-ellipsis"
                                    >
                                        <b>Raw unit:</b>{" "}
                                        {dataSource.rawUnitsSuffixHtml ?? "--"}
                                    </Typography>
                                </div>
                                <DataSourceCalculationCards 
                                    {...dataSource } 
                                    confidenceName={confidenceName as ConfidenceName} 
                                    confidenceReason={dataSource.currentCalculationConfidenceReason as string}
                                     />
                            </div>
                        </>
                    ) : (
                        <div className="flex justify-center bg-surface-light-1 rounded-xl p-6">
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-surface-dark">
                                    <div>No emissions factor set</div>
                                    <WarningCircle size={20} />
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={setCalculationDialogState.open}
                                    className="mx-auto"
                                >
                                    Set emissions factor
                                </Button>
                            </div>
                        </div>
                    )}
                    <RequireEmissionsSourceEvidenceTable
                        defaultStatusFilter="All"
                        emissionsSourceId={dataSource.emissionsSourceId}
                        noEvidenceMessage="No evidence found"
                        title="Emissions Source Evidence"
                    />
                    <DialogBox
                        title="New emissions factor"
                        {...setCalculationDialogState}
                    >
                        <SetCalculationForm
                            locationName={dataSource.locationName}
                            dataSourceName={dataSource.name}
                            isLoading={isSettingCalculation}
                            isOpen={setCalculationDialogState.isOpen}
                            onCancel={setCalculationDialogState.close}
                            onSubmit={handleSetCalculationFormSubmission}
                        />
                    </DialogBox>
                </ContentContainer>
            )}
        </MainLayout>
    );
};

export default DataSourceDetailPage;
